// Settings → API: keys for the public REST API, and enough reference to use
// them. The raw token appears exactly once, in the creation modal — after
// that the list shows only the prefix, which is the whole point of hashing.
import { useEffect, useState } from 'react';
import { ApiKeySummary, createApiKeyApi, listApiKeysApi, revokeApiKeyApi } from '../../api/admin';
import { PlanLimitBody, planLimitOf } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { CodeBlock, Modal, TableCard } from '../../components/fs';
import { PHONE, useMediaQuery } from '../../shared/media';
import WebhookSettings from './WebhookSettings';

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function ApiSettings() {
  const [keys, setKeys] = useState<ApiKeySummary[] | null>(null);
  const [apiEnabled, setApiEnabled] = useState(true);
  const [error, setError] = useState('');
  const [limitHit, setLimitHit] = useState<PlanLimitBody | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [minted, setMinted] = useState<{ name: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<number | null>(null);
  /** a card per key instead of five columns (§4G G7) */
  const phone = useMediaQuery(PHONE);
  const [busy, setBusy] = useState(false);

  const load = () => {
    listApiKeysApi()
      .then((r) => { setKeys(r.keys); setApiEnabled(r.apiEnabled); })
      .catch((e) => setError(e.message || 'Could not load API keys.'));
  };
  useEffect(load, []);

  async function create() {
    if (!name.trim()) return;
    setBusy(true); setError('');
    try {
      const r = await createApiKeyApi(name.trim());
      setMinted({ name: r.key.name, token: r.token });
      setCreateOpen(false); setName('');
      load();
    } catch (e: any) {
      const limit = planLimitOf(e);
      if (limit) { setLimitHit(limit); setCreateOpen(false); }
      else setError(e.message || 'Could not create the key.');
    } finally { setBusy(false); }
  }

  async function revoke(id: number) {
    setBusy(true);
    try { await revokeApiKeyApi(id); setConfirmRevoke(null); load(); }
    catch (e: any) { setError(e.message || 'Revoke failed.'); }
    finally { setBusy(false); }
  }

  const live = (keys || []).filter((k) => !k.revoked_at);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const curl = `# who this key belongs to (a connection test)\ncurl ${origin}/api/v1/me \\\n  -H "Authorization: Bearer $FM_KEY"`;

  return (
    <div className="st-stack">
      <div className="fs-page-head">
        <div className="text">
          <h1>API keys</h1>
          <div className="fs-page-sub">
            Keys let scripts, Zapier and Make read submissions, create forms and subscribe webhooks
            on this account's behalf. A key is account-wide — treat it like a password.
          </div>
        </div>
        {apiEnabled && (
          <button className="fs-btn primary" onClick={() => { setCreateOpen(true); setError(''); }}>Create key</button>
        )}
      </div>
      {error && <div className="ad-err">{error}</div>}
      {limitHit && <PlanLimitBlock body={limitHit} />}

      {!apiEnabled && !limitHit && (
        <div className="fs-consequence">
          API access is not part of the current plan. Existing keys stay listed but stop answering;
          upgrading turns them back on.
        </div>
      )}

      <TableCard>
      {!keys ? (
        <div className="st-card-body st-muted">Loading…</div>
      ) : live.length === 0 ? (
        <div className="st-card-body st-muted">No API keys yet.</div>
      ) : (
        phone ? (
          <div className="api-cards">
            {live.map((k) => (
              <div className="api-card" key={k.id}>
                <div className="head"><span className="name">{k.name}</span></div>
                <div className="key"><code>{k.token_prefix}…</code></div>
                <div className="when">Full access · created {when(k.created_at)} · last used {when(k.last_used_at)}</div>
                <div className="acts">
                  {confirmRevoke === k.id ? (
                    <>
                      <button className="fs-btn danger sm" disabled={busy} onClick={() => void revoke(k.id)}>Confirm revoke</button>
                      <button className="fs-btn sm" disabled={busy} onClick={() => setConfirmRevoke(null)}>Cancel</button>
                    </>
                  ) : (
                    <button className="fs-btn text danger" onClick={() => setConfirmRevoke(k.id)}>Revoke</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
        <table className="fs-table">
          <thead><tr><th>Name</th><th>Key</th><th>Can</th><th>Created</th><th>Last used</th><th className="fs-row-actions" /></tr></thead>
          <tbody>
            {live.map((k) => (
              <tr key={k.id}>
                <td style={{ fontWeight: 500 }}>{k.name}</td>
                <td><code className="st-mono">{k.token_prefix}…</code></td>
                {/* Keys have no scopes (§4): say what is true. */}
                <td>Full access</td>
                <td style={{ whiteSpace: 'nowrap' }} className="st-muted">{when(k.created_at)}</td>
                <td style={{ whiteSpace: 'nowrap' }} className="st-muted">{when(k.last_used_at)}</td>
                <td className="fs-row-actions">
                  {confirmRevoke === k.id ? (
                    <>
                      <button className="fs-btn danger sm" disabled={busy} onClick={() => void revoke(k.id)}>Confirm revoke</button>
                      <button className="fs-btn sm" disabled={busy} onClick={() => setConfirmRevoke(null)}>Cancel</button>
                    </>
                  ) : (
                    <button className="fs-btn text danger" onClick={() => setConfirmRevoke(k.id)}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )
      )}
      </TableCard>

      <CodeBlock code={curl} label="cURL example" className="api-curl" />

      <TableCard
        title="Using the API"
        hint={<>Send the key as a bearer token. Webhook deliveries are signed with <code>X-Signature: sha256=…</code> when the subscription carries a secret.</>}
      >
      <table className="fs-table stack api-ref">
        <tbody>
          <tr><td><code>GET /api/v1/me</code></td><td>Who the key belongs to (connection test).</td></tr>
          <tr><td><code>GET /api/v1/forms</code></td><td>List forms.</td></tr>
          <tr><td><code>GET /api/v1/forms/:id</code></td><td>One form with its published definition.</td></tr>
          <tr><td><code>POST /api/v1/forms</code></td><td>Create a draft form <code>{'{ name }'}</code>.</td></tr>
          <tr><td><code>GET /api/v1/forms/:id/submissions</code></td><td>Newest first; <code>?limit&amp;cursor&amp;since</code>.</td></tr>
          <tr><td><code>GET /api/v1/submissions/:id</code></td><td>One submission by its SUB-… id.</td></tr>
          <tr><td><code>POST /api/v1/forms/:id/webhooks</code></td><td>Subscribe <code>{'{ url, secret? }'}</code> to new submissions.</td></tr>
          <tr><td><code>DELETE /api/v1/webhooks/:id</code></td><td>Unsubscribe.</td></tr>
        </tbody>
      </table>
      </TableCard>

      <WebhookSettings />

      {createOpen && (
        <Modal title="Create API key" narrow onClose={() => setCreateOpen(false)}
          footer={<>
            <button className="fs-btn primary" disabled={busy || !name.trim()} onClick={() => void create()}>Create</button>
            <button className="fs-btn neutral" onClick={() => setCreateOpen(false)}>Cancel</button>
          </>}>
          <div className="ad-field"><label>Name</label>
            <input className="fs-input" autoFocus placeholder="e.g. Zapier" value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void create(); }} />
            <small>What this key is for — shown in the list, changeable never.</small>
          </div>
        </Modal>
      )}

      {minted && (
        <Modal title={`Key created: ${minted.name}`} narrow onClose={() => setMinted(null)}
          footer={<button className="fs-btn primary" onClick={() => setMinted(null)}>Done</button>}>
          <p style={{ fontSize: 13.5, color: 'var(--ink-700)', marginTop: 0 }}>
            Copy it now — <b>this is the only time it will be shown.</b> The server keeps only a hash.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={{ background: 'var(--canvas)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 12, wordBreak: 'break-all', flex: 1 }}>
              {minted.token}
            </code>
            <button className="fs-btn" onClick={() => {
              navigator.clipboard?.writeText(minted.token).then(() => setCopied(true)).catch(() => undefined);
            }}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
